"use client";
import { useTranslations } from "@zapier/i18n";

import { useEffect, useMemo, useState } from "react";
import { Avatar, Key, Text } from "@zapier/design-system";
import {
  useCurrentUser,
  useFolderActionPermissions,
  useMembers,
  useQuerySearchMembers,
} from "@zapier/client";
import { AssetTypes } from "@zapier/utils";

import { FilterTypeahead } from "../FilterTypeahead";
import {
  ConnectionSearchInteractionOption,
  FolderSearchInteractionOptions,
  useEmitConnectionSearchInteractionEvent,
  useEmitFolderSearchInteractionEvent,
  useEmitZapSearchInteractionEvent,
  ZapSearchInteractionOptions,
} from "../../../events";
import { useRouterParams } from "../../../hooks";

import styles from "./OwnerFilter.module.css";
import { usePathname } from "next/navigation";

interface Props {
  assetType: AssetTypes;
  isLabelHidden?: boolean;
}

function OwnerFilter({ assetType, isLabelHidden }: Props) {
  const t = useTranslations("owner-filter");

  const { owner, shared, setParams, folderType } = useRouterParams();
  const { canFilterOwners } = useFolderActionPermissions({});
  const pathname = usePathname();

  const { emit: emitZapSearchEvent } = useEmitZapSearchInteractionEvent();
  const { emit: emitFolderSearchEvent } = useEmitFolderSearchInteractionEvent();
  const { emit: emitConnectionSearchEvent } =
    useEmitConnectionSearchInteractionEvent();

  // Disable this input if the shared filter is
  // 1. Set to "me" OR
  // 2. Trying to search for ownedByMe folders OR
  // 3. Trying to search for personal folders and is not a account owner, super admin or admin
  const isDisabled =
    shared === "me" ||
    folderType === "ownedByMe" ||
    (pathname?.includes("folders") &&
      folderType === "personal" &&
      !canFilterOwners);

  const [searchValue, setSearchValue] = useState<string>("");
  const [ownerValue, setOwnerValue] = useState<number | undefined>();
  const [selectedKey, setSelectedKey] = useState<Key>("");

  const { members } = useQuerySearchMembers({
    search: searchValue,
    allowTeams: true,
  });

  const { user } = useCurrentUser();

  const myId = user?.id;

  // Single useMembers call to fetch both the URL-driven owner (ownerValue)
  // and the current user (myId, when not actively searching).
  const memberLookupIds = [
    ...(ownerValue ? [ownerValue] : []),
    ...(!searchValue && myId !== undefined ? [myId] : []),
  ];
  const { members: membersById } = useMembers({
    ids: [...new Set(memberLookupIds)],
  });

  const items = useMemo(() => {
    const meLabel = t("placeholders.me");

    const ownerSuggestions: ((typeof members)[number] & {
      name: string;
      displayName?: string;
    })[] = members.map((member) => ({
      ...member,
      id: member.id,
      name: member.fullName,
    }));
    // When the URL contains `?owner=<id>` but that person isn't in the search
    // results, prepend them from `membersById` so the dropdown still shows them.
    if (
      ownerValue &&
      membersById &&
      !searchValue &&
      !ownerSuggestions.some(({ id }) => id === ownerValue)
    ) {
      const member = membersById[ownerValue];
      if (member) {
        ownerSuggestions.unshift({
          ...member,
          id: member.id,
          name: member.fullName,
          ...(member.id === myId && { displayName: meLabel }),
        });
      }
    }

    if (myId === undefined) {
      return ownerSuggestions;
    }

    // If the current user is in the list, move them to the top and label the row "Me".
    // `name` is kept as fullName so the typeahead textValue still matches real name searches.
    const selfIndex = ownerSuggestions.findIndex((m) => m.id === myId);
    if (selfIndex !== -1) {
      const [self] = ownerSuggestions.splice(selfIndex, 1);
      ownerSuggestions.unshift({ ...self, displayName: meLabel });
    } else if (!searchValue && membersById?.[myId]) {
      // Current user isn't in the paginated search results (API returns max 15).
      // Inject them at the top from the dedicated member fetch so they always
      // appear in the default (non-search) dropdown.
      const me = membersById[myId];
      ownerSuggestions.unshift({
        ...me,
        name: me.fullName,
        displayName: meLabel,
      });
    }

    return ownerSuggestions;
  }, [members, ownerValue, membersById, searchValue, myId, t]);

  useEffect(() => {
    if (owner && Number(owner)) {
      setOwnerValue(Number(owner));
      setSelectedKey(Number(owner));
    } else {
      // Clearing ownerValue stops useMembers from pre-fetching that member,
      // but React Query caches the response so re-selecting them is still fast.
      // Without this, the stale ownerValue keeps the old member in
      // memberLookupIds, causing membersById to get a new reference when
      // searchValue clears — which triggers the re-affirm effect and
      // re-selects the owner the user just cleared.
      setOwnerValue(undefined);
      setSearchValue("");
      setSelectedKey("");
    }
  }, [owner]);

  // Once membersById loads for a URL-driven owner, re-affirm the selection so
  // the typeahead can display it. Guarded by `owner` so it doesn't re-select
  // after a reset.
  useEffect(() => {
    if (owner && membersById && ownerValue) {
      setSelectedKey(ownerValue);
    }
  }, [owner, membersById, ownerValue]);

  // callback fired when a key is selected from the dropdown
  const selectKey = (key: Key | null) => {
    setSelectedKey(key || "");
    if (String(owner) !== String(key)) {
      if (!key) {
        // if there is no key, clear the owner
        setParams({ page: 1 }, ["owner"]);
        return;
      }

      if (assetType === AssetTypes.Folder) {
        emitFolderSearchEvent({
          optionSelected: FolderSearchInteractionOptions.Owner,
        });
      }

      if (assetType === AssetTypes.Zap) {
        emitZapSearchEvent({
          optionSelected: ZapSearchInteractionOptions.Owner,
        });
      }

      if (assetType === AssetTypes.Connection) {
        emitConnectionSearchEvent(ConnectionSearchInteractionOption.Owner);
      }

      setOwnerValue(Number(key));
      setParams({ owner: key, page: 1 });
    }
  };

  const searchChange = (search: string) => {
    setSearchValue(search);
  };

  // if shared changes to "me" or folderType === personal and user cannot filter owners, reset the selected key
  useEffect(() => {
    if (shared === "me" || (folderType === "personal" && !canFilterOwners)) {
      setSelectedKey("");
    }
  }, [shared, folderType, canFilterOwners]);

  const placeholder =
    shared === "me" || folderType === "personal"
      ? t("placeholders.me")
      : t("placeholders.all-owners");

  return (
    <FilterTypeahead
      isDisabled={isDisabled}
      isLabelHidden={isLabelHidden}
      items={items}
      label={t("labels.owner")}
      menuWidth="22ch" // wider to support user's names
      onSearchChange={searchChange}
      onSelectKey={selectKey}
      placeholder={placeholder}
      selectedKey={selectedKey}
      renderItem={(item: any) => {
        return (
          <div className={styles.menuItem}>
            <Avatar url={item.photoUrl} name={item.fullName} />
            <Text tag="p">{item.displayName ?? item.name}</Text>
          </div>
        );
      }}
    />
  );
}

export default OwnerFilter;
